import { ApiError } from '@/lib/api/errors'
import { TIMEOUT_AVISO_SAIDA_MS, logout, logoutAll, type OpcoesDeSaida } from '@/services/auth'
import { esquemaDe } from './acesso'
import { renovarDentroDaOperacao } from './renovacao'
import { segurarTrinco } from './trinco'

export type RotaDeSaida = 'logout' | 'logout-all'
/** `confirmada` = 204, ou a sessão já estava morta (renovação 401). Só aí o cookie de renovação morreu. */
export type ConfirmacaoDeSaida = 'confirmada' | 'nao-confirmada'

/**
 * Avisa o servidor do "Sair"/"Sair de todos" DENTRO do trinco entre abas (faixa 25 §2, §6). Nunca rejeita.
 * Ver `saidaNoServidor.md`.
 */
export function sairNoServidor(rota: RotaDeSaida): Promise<ConfirmacaoDeSaida> {
  // O teto de 3 s cobre o pedido INTEIRO (spec §2, D9): espera pelo trinco + logout + renovação + repetição.
  // Trinco não concedido no teto (outra aba renovando ou trocando a senha) → AbortError → `nao-confirmada`.
  const prazo = Date.now() + TIMEOUT_AVISO_SAIDA_MS
  return segurarTrinco(() => avisarDentroDoTrinco(rota, prazo), { prazoMs: TIMEOUT_AVISO_SAIDA_MS }).catch(
    (erro: unknown) => naoConfirmada(erro),
  )
}

async function avisarDentroDoTrinco(rota: RotaDeSaida, prazo: number): Promise<ConfirmacaoDeSaida> {
  // Cada pedido leva o que sobrou do teto depois da espera pelo trinco.
  const restante = () => Math.max(1, prazo - Date.now())
  const avisar = (acesso?: OpcoesDeSaida['acesso']) => (rota === 'logout' ? logout : logoutAll)({ acesso, timeoutMs: restante() })
  try {
    await avisar()
    return 'confirmada'
  } catch (erro) {
    if (!(erro instanceof ApiError && erro.status === 401)) return naoConfirmada(erro)
  }
  // 401: access vencido (C5) — o logout exige access válido e o 401 NÃO apaga o cookie. Renova DIRETO: pedir o
  // trinco de novo enfileiraria atrás de nós mesmos e o "Sair" nunca terminaria (§2 "sem reentrada").
  const renovada = await renovarDentroDaOperacao(restante())
  if (renovada.tipo === 'sessao-morta') return 'confirmada'
  // 503/rede, ou fora da rede permitida (cookie vivo, faixa 28): não confirmou — fica a saída pendente.
  if (renovada.tipo === 'indisponivel' || renovada.tipo === 'rede-bloqueada') return 'nao-confirmada'
  try {
    // O access novo vai SÓ neste pedido: não entra na memória de quem está saindo (decisão 4).
    await avisar({ token: renovada.acesso.access_token, esquema: esquemaDe(renovada.acesso.token_type) })
    return 'confirmada'
  } catch (erro) {
    return naoConfirmada(erro)
  }
}

function naoConfirmada(erro: unknown): ConfirmacaoDeSaida {
  const descricao = erro instanceof ApiError ? `HTTP ${erro.status} ${erro.code}` : erro instanceof Error ? erro.name : 'erro desconhecido'
  console.warn('Aviso de saída ao servidor não confirmado; fica a saída pendente.', descricao)
  return 'nao-confirmada'
}
